import { computed, ref } from "vue";
import { DraggerType } from "@/drawing";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import DraggerMenuState from "./DraggerMenuState";
import { setRulerVisible } from "./RulerState";
import CanvasSettingsState from "./CanvasSettingsState";
import { trackAppSetting } from "@/analytics/app-analytics";
import {
  TOOLBAR_COLUMNS_MODES,
  type ToolbarColumnsMode,
} from "@/utils/toolbar-layout";
import {
  TOOLBAR_GROUPS,
  sanitizeHiddenTools,
  visibleGroups,
  type ToolbarGroupId,
  type ToolbarToolId,
} from "@/utils/toolbar-tools";

/**
 * Outils masqués de la barre d'outils (modale Affichage). Par appareil, comme
 * la taille de l'interface : c'est une affaire d'écran et d'habitude.
 */

function readHiddenTools(): ToolbarToolId[] {
  try {
    const stored = $ls.getItem(LocalStorageKey.CANVAS_HIDDEN_TOOLS);
    return stored ? sanitizeHiddenTools(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
}

export const hiddenTools = ref<ToolbarToolId[]>(readHiddenTools());

/** Outils de dessin de chaque bouton (le texte et ses variantes stylet). */
const TOOL_DRAGGERS: Partial<Record<ToolbarToolId, DraggerType[]>> = {
  select: [DraggerType.Select],
  eraser: [DraggerType.Eraser],
  freehand: [DraggerType.Freehand],
  text: [DraggerType.Text, DraggerType.Handwriting, DraggerType.StylusKeyboard],
  line: [DraggerType.Line],
  rectangle: [DraggerType.Rectangle],
  ellipse: [DraggerType.Ellipse],
  polyline: [DraggerType.Polyline],
  origin: [DraggerType.Origin],
  zoomWindow: [DraggerType.ZoomWindow],
};

/**
 * Un outil masqué ne doit pas rester actif sans bouton pour le voir ni le
 * couper : l'outil de dessin repasse en navigation, l'aide au tracé s'éteint.
 * Appelé à chaque masquage et à l'ouverture d'un plan (état enregistré).
 */
export function releaseHiddenTools() {
  const isHidden = (tool: ToolbarToolId) => hiddenTools.value.includes(tool);
  const active = DraggerMenuState.activeDragger.name;
  if (hiddenTools.value.some((tool) => TOOL_DRAGGERS[tool]?.includes(active))) {
    DraggerMenuState.setActiveDragger({ name: DraggerType.None });
  }
  // Sans le bouton Règle, plus moyen de masquer les règles.
  if (isHidden("origin") && DraggerMenuState.rulerVisible) {
    setRulerVisible(false);
  }
  if (isHidden("objectSnap") && DraggerMenuState.objectSnapEnabled) {
    DraggerMenuState.toggleObjectSnap();
  }
  if (isHidden("ortho") && DraggerMenuState.orthoMode) {
    DraggerMenuState.setOrthoMode(false);
  }
  if (isHidden("polar") && DraggerMenuState.polarMode) {
    DraggerMenuState.setPolarMode(false);
    CanvasSettingsState.updateAndPersist();
  }
}

function setHiddenTools(tools: ToolbarToolId[]) {
  hiddenTools.value = sanitizeHiddenTools(tools);
  try {
    $ls.setItem(
      LocalStorageKey.CANVAS_HIDDEN_TOOLS,
      JSON.stringify(hiddenTools.value),
    );
  } catch {
    // Stockage indisponible (navigation privée…) : le choix vaut pour la session.
  }
  releaseHiddenTools();
}

/** Groupes de la barre réduits à leurs outils visibles (les vides disparaissent). */
export const visibleToolbarGroups = computed(() =>
  visibleGroups(hiddenTools.value),
);

export function isToolVisible(tool: ToolbarToolId): boolean {
  return !hiddenTools.value.includes(tool);
}

export function setToolVisible(tool: ToolbarToolId, visible: boolean) {
  const others = hiddenTools.value.filter((hidden) => hidden !== tool);
  setHiddenTools(visible ? others : [...others, tool]);
}

function groupTools(groupId: ToolbarGroupId): readonly ToolbarToolId[] {
  return TOOLBAR_GROUPS.find((group) => group.id === groupId)?.tools ?? [];
}

/** Un groupe est affiché tant qu'au moins un de ses outils l'est. */
export function isGroupVisible(groupId: ToolbarGroupId): boolean {
  return groupTools(groupId).some(isToolVisible);
}

/** Affiche ou masque tous les outils du groupe. */
export function setGroupVisible(groupId: ToolbarGroupId, visible: boolean) {
  const tools = groupTools(groupId);
  const others = hiddenTools.value.filter((hidden) => !tools.includes(hidden));
  setHiddenTools(visible ? others : [...others, ...tools]);
}

export function showAllTools() {
  setHiddenTools([]);
}

// ── Colonnes de la barre (modale Affichage), par appareil elles aussi ───────

function readColumnsMode(): ToolbarColumnsMode {
  try {
    const stored = $ls.getItem(LocalStorageKey.CANVAS_TOOLBAR_COLUMNS);
    return TOOLBAR_COLUMNS_MODES.find((mode) => mode === stored) ?? "auto";
  } catch {
    return "auto";
  }
}

/** « auto » : une colonne, deux quand la hauteur manque ; « two » : deux. */
export const toolbarColumnsMode = ref<ToolbarColumnsMode>(readColumnsMode());

export function setToolbarColumnsMode(mode: ToolbarColumnsMode) {
  if (mode === toolbarColumnsMode.value) return;
  toolbarColumnsMode.value = mode;
  trackAppSetting("canvas_toolbar_columns", mode);
  try {
    $ls.setItem(LocalStorageKey.CANVAS_TOOLBAR_COLUMNS, mode);
  } catch {
    // Stockage indisponible : le choix vaut pour la session.
  }
}
